/**
 * Client Management Help Content
 *
 * Covers client CRUD, contact details, industry classification,
 * and client-project linking.
 */

import type { HelpContent } from '../types';
import {
  createHelpContent,
  createSection,
  createFAQ,
  createTip,
  createWalkthrough,
  createTroubleshooting,
  createRelatedLink,
  createFieldHelp,
} from '../utils';

// =============================================================================
// MAIN CLIENTS HELP
// =============================================================================

export const clientsHelp: HelpContent = createHelpContent({
  id: 'clients',
  title: 'Client Management',
  description: 'Manage client records, contact details, and client-project relationships',
  lastUpdated: '2026-02-08',
  version: '1.0',
  visibility: { visibleTo: ['admin'] },

  tips: [
    createTip(
      'tip-projects-link',
      'info',
      'Client-Project Link',
      'Clients can be linked to projects during project creation. This helps track which work is for which client and enables client-level reporting.'
    ),
    createTip(
      'tip-delete-restriction',
      'warning',
      'Deletion Restrictions',
      'You cannot delete a client that has linked projects. Remove or reassign the project associations first.'
    ),
  ],

  sections: [
    createSection(
      'overview',
      'Overview',
      `<p>The Clients page lets admins manage the organization's client records. Each client can be linked to one or more projects for tracking and reporting purposes.</p>
      <p>Key features:</p>
      <ul>
        <li><strong>Client Records</strong> — Create, view, edit, and delete client information</li>
        <li><strong>Contact Details</strong> — Store email, phone, website, and address</li>
        <li><strong>Industry Classification</strong> — Categorize clients by industry</li>
        <li><strong>Project Linking</strong> — Associate clients with projects</li>
        <li><strong>Search & Filter</strong> — Find clients by name, industry, or description</li>
      </ul>`
    ),
    createSection(
      'views',
      'Grid & List Views',
      `<p>Toggle between two display modes:</p>
      <ul>
        <li><strong>Grid View</strong> — Cards showing client details at a glance with icons</li>
        <li><strong>List View</strong> — Table format with columns for name, industry, contact, website, and address</li>
      </ul>
      <p>Use the view toggle buttons at the top of the page to switch between modes.</p>`
    ),
    createSection(
      'creating',
      'Creating a Client',
      `<p>Click the "Add Client" button to open the creation form. Fill in:</p>
      <ul>
        <li><strong>Client Name</strong> (required) — The client's company or organization name</li>
        <li><strong>Industry</strong> — Select from the predefined list (e.g., Technology, Healthcare, Finance)</li>
        <li><strong>Description</strong> — Notes or description about the client</li>
        <li><strong>Contact Email</strong> — Primary contact email address</li>
        <li><strong>Contact Phone</strong> — Primary phone number</li>
        <li><strong>Website</strong> — Client's website URL</li>
        <li><strong>Address</strong> — Physical address</li>
      </ul>`
    ),
    createSection(
      'editing',
      'Editing & Deleting',
      `<p>From the client card or list row:</p>
      <ul>
        <li><strong>Edit</strong> — Update any client field. Changes take effect immediately.</li>
        <li><strong>Delete</strong> — Remove the client record. This is only possible if the client has no linked projects. If projects exist, you'll see an error listing the linked projects.</li>
      </ul>`
    ),
    createSection(
      'search',
      'Search & Filtering',
      '<p>Use the search bar to filter clients in real time. The search matches against client name, industry, and description fields. Results update as you type.</p>'
    ),
    createSection(
      'project-integration',
      'Client-Project Integration',
      `<p>Clients are linked to projects through the project creation form:</p>
      <ul>
        <li>When creating a new project, select an optional client from the dropdown</li>
        <li>The client name appears on project cards for easy identification</li>
        <li>Internal projects can be created without a client association</li>
      </ul>`
    ),
  ],

  walkthroughs: [
    createWalkthrough(
      'walkthrough-add-client',
      'How to Add a New Client',
      'Create a new client record in the system.',
      [
        {
          title: 'Navigate to Clients',
          description: 'Go to the Clients page from the Management section in the sidebar.',
        },
        {
          title: 'Click "Add Client"',
          description: 'Open the client creation form.',
        },
        {
          title: 'Enter client details',
          description: 'Fill in the client name (required), industry, description, and contact information.',
        },
        {
          title: 'Save',
          description: 'Click Save to create the client. It will appear in the client list immediately.',
        },
      ],
      1
    ),
    createWalkthrough(
      'walkthrough-link-project',
      'How to Link a Client to a Project',
      'Associate a client with a project during project creation.',
      [
        {
          title: 'Go to Projects',
          description: 'Navigate to the Projects page.',
        },
        {
          title: 'Create a new project',
          description: 'Click "New Project" to open the project creation form.',
        },
        {
          title: 'Select a client',
          description: 'In the Client field, select the client from the dropdown list.',
        },
        {
          title: 'Complete and save',
          description: 'Fill in the remaining project details and save. The project will now be linked to the client.',
        },
      ],
      2
    ),
  ],

  troubleshooting: [
    createTroubleshooting(
      'trouble-cant-delete',
      'Cannot delete a client',
      [
        'The client has one or more projects linked to it',
        'Projects must be reassigned or removed before the client can be deleted',
      ],
      [
        {
          description: 'Remove project associations',
          steps: [
            'Note the project names shown in the error message',
            'Go to each linked project and either change the client or remove the client association',
            'Return to Clients and try deleting again',
          ],
        },
      ]
    ),
  ],

  faqs: [
    createFAQ(
      'faq-who-can-manage',
      'Who can manage clients?',
      'Client management (create, edit, delete) is restricted to admin users. All authenticated users can view client details.',
      ['admin', 'permission', 'access', 'who']
    ),
    createFAQ(
      'faq-industry-options',
      'What industry options are available?',
      'The system provides a predefined list of industries including Technology, Healthcare, Finance, Manufacturing, Retail, and more. Contact an admin if you need an industry added.',
      ['industry', 'category', 'options', 'list']
    ),
    createFAQ(
      'faq-link-multiple',
      'Can a client have multiple projects?',
      'Yes, multiple projects can be linked to the same client. Each project can only have one client, but a client can have many projects.',
      ['multiple', 'projects', 'link', 'many']
    ),
    createFAQ(
      'faq-internal-projects',
      'Do all projects need a client?',
      'No. The client field is optional when creating a project. Internal projects or administrative work can be created without a client association.',
      ['internal', 'optional', 'no client', 'required']
    ),
    createFAQ(
      'faq-delete-projects',
      'Why can\'t I delete this client?',
      'Clients with linked projects cannot be deleted to prevent data integrity issues. Reassign or remove the project associations first, then delete the client.',
      ['delete', 'cannot', 'error', 'projects']
    ),
    createFAQ(
      'faq-client-reports',
      'Can I get reports by client?',
      'Project analytics and effort reports can be filtered by project, which are linked to clients. This gives you visibility into work done for each client.',
      ['reports', 'analytics', 'filter', 'client']
    ),
  ],

  relatedLinks: [
    createRelatedLink('Projects', '/projects', { description: 'View and manage projects linked to clients' }),
    createRelatedLink('Admin', '/admin', { description: 'User and system management' }),
    createRelatedLink('Analytics', '/analytics', { description: 'Project and effort analytics' }),
  ],

  keywords: [
    'clients',
    'customers',
    'client management',
    'contacts',
    'industry',
    'project linking',
    'companies',
    'organizations',
  ],
});

// =============================================================================
// FIELD-LEVEL HELP
// =============================================================================

export const clientFieldHelp = {
  name: createFieldHelp(
    'clients:form:name',
    'Client Name',
    'The official name of the client company or organization. This appears on project cards and in reports.',
    { example: 'Acme Corporation' }
  ),
  industry: createFieldHelp(
    'clients:form:industry',
    'Industry',
    'Select the industry that best describes this client\'s sector. Used for categorization and filtering.',
    { example: 'Technology' }
  ),
  contactEmail: createFieldHelp(
    'clients:form:contactEmail',
    'Contact Email',
    'The primary email address for contacting this client. Must be a valid email format.',
    { example: 'contact@acmecorp.com' }
  ),
  website: createFieldHelp(
    'clients:form:website',
    'Website',
    'The client\'s company website URL.',
    { example: 'https://www.acmecorp.com' }
  ),
};
